import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";

import { useBrandAssets } from "@/entities/brand-asset";
import { toast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { DragOverlayCanvas } from "@/shared/ui/overlay/DragOverlayCanvas";
import { AppearanceTab } from "../../ui/components/editor/AppearanceTab";
import { AttachLogoButton, AttachQrButton } from "../../ui/components/editor/AttachBrandButtons";
import { I2IEditTab } from "../../ui/components/editor/I2IEditTab";
import { RegenerateTab } from "../../ui/components/editor/RegenerateTab";
import { filterToCss } from "../../ui/components/canvas/filterCss";
import { posterService, type FilterConfig, type PosterAssetDto } from "../../api/posterService";
import {
  DEFAULT_OVERLAY,
  type OverlayConfig as OverlayConfigV6,
} from "../../api/posterTemplateService";
import { BlockInspector } from "./BlockInspector";
import { contentFromProps } from "../model/canvasBridge";
import type { LongImageBlockForm } from "../model/blocks";
import { errorToast } from "@/shared/lib/apiErrors";

/**
 * 长图「改这一块」全屏编辑器(2026-08-08)。
 *
 * ═══ 从 560 抽屉换成全屏弹框 ═══════════════════════════════════════════════════
 * 上一版(方案 C)只露「画面调整」一件事,理由是「AI 改图/AI 重画 对分段来说就是带提示词的
 * 单块重出,和块行那颗『重出』重叠」。**用户不认这个取舍** —— 因为分段里的文案块/参数块是
 * PIL 模板渲染的,`AI 重画` 对它们毫无意义,而用户想改的正是那些字;绕到「回搭建器改这一稿」
 * 那条路又回填不上。于是「改这一块」变成一个改不了内容的编辑器。
 *
 * 这一版的取舍反过来:**块行那颗「重出」并进编辑器**,四件事收在一个入口里,按块类型露:
 *
 * | 块 | 画面调整 | 改内容 | AI 改图 | AI 重画 |
 * |---|---|---|---|---|
 * | 图块 | 拖 logo + 滤镜 | 改文案 → **付费**重出 | i2i 带提示词 | t2i 带提示词 |
 * | 文字 / 数据块 | 滤镜(太扁不给拖) | 改文案 → **零积分**重渲 | ✗ | ✗ |
 *
 * ★文字块不给 AI 两 tab 不是偷懒:它们是 PIL 确定性渲染的,i2i/t2i 对一条模板文字带
 *   要么无从下手、要么把它换成一张 AI 图 —— 那不是「改字」,是「把这一块弄没了」。
 *
 * ═══ 两件刻意不渲的 ═══════════════════════════════════════════════════════════
 * ★**版本链**:长图单块重出是**原地换 seq**(分段 seq = 块序+2 的硬契约),不产生
 *   `originAssetId` 版本链。硬塞会显示一条永远只有一项的假版本链。
 * ★**批量品牌变体**:它按 `max(seq)+1` 插新资产,会直接撞坏分段 seq 契约。
 */

/** 低于这个宽高比(w/h)才给拖拽;再扁就只给滤镜。3.0 ≈ 480 宽时高 160。 */
const DRAGGABLE_MAX_ASPECT = 3;

type TabKey = "appearance" | "content" | "edit" | "redraw";

export function SegmentEditorDialog({
  open,
  onOpenChange,
  taskId,
  asset,
  blockName,
  blockSpec,
  onSaved,
  regenCredits = null,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  taskId: number;
  /** 这一块的分段资产(baseUrl / 真实像素 / 已存 overlay 都在它身上)。 */
  asset: PosterAssetDto;
  blockName: string;
  /**
   * 这一块在**这一稿**里的定义(primitive / styleTag / props)——
   * 来自 `readLongImageSpec`,即真正发给引擎的那份内容。
   *
   * ★拿不到就只露「画面调整」:没有 props 就渲不出改内容的表单,
   *   而渲一个空表单让用户填、填完覆盖掉真内容,比不给这个 tab 糟得多。
   */
  blockSpec?: LongImageBlockForm | null;
  /** 保存 / 派发成功 → 让详情页把轮询拉起来(整图会换一版)。 */
  onSaved: () => void;
  /**
   * AI 改图 / AI 重画 这一笔的积分(修复波 0926 · 代理 H)。口径 = 单块重出按 1 张、
   * `longimage.render` × 这一稿的渠道×档位(见 `longimage/model/blockRegenQuote`)。
   * null = 报不出价 → 按钮上不写数字(不兜旧值、不兜 0)。
   */
  regenCredits?: number | null;
}) {
  const { data: brandAssets } = useBrandAssets();
  const [overlay, setOverlay] = useState<OverlayConfigV6>(() => readOverlay(asset));
  const [filter, setFilter] = useState<FilterConfig>(asset.currentFilterConfig ?? {});
  const [comparing, setComparing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<TabKey>("appearance");
  /** 改内容 tab 的在编 props(与 blockSpec 分开,取消就是丢弃它)。 */
  const [draftProps, setDraftProps] = useState<Record<string, unknown> | null>(null);

  const isImageBlock = blockSpec?.primitive === "image";
  const canEditContent = blockSpec != null;

  const aspect = useMemo(() => {
    const w = asset.actualWidth ?? 0;
    const h = asset.actualHeight ?? 0;
    return w > 0 && h > 0 ? w / h : 1;
  }, [asset.actualWidth, asset.actualHeight]);
  const draggable = aspect <= DRAGGABLE_MAX_ASPECT;

  const logoUrl = useMemo(
    () => brandAssets?.logos.find((l) => l.objectKey === overlay.logoUploadId)?.signedUrl ?? null,
    [brandAssets, overlay.logoUploadId]
  );
  const qrUrl = useMemo(
    () =>
      overlay.qrCodeMode === "UPLOAD"
        ? (brandAssets?.qrs.find((q) => q.objectKey === overlay.qrCodeUploadId)?.signedUrl ?? null)
        : null,
    [brandAssets, overlay.qrCodeMode, overlay.qrCodeUploadId]
  );

  /** ① 覆盖这一段的成品(写 finalKey)② 整图是拼出来的,必须再拼一次。 */
  async function saveAppearance() {
    setBusy(true);
    try {
      // ★2026-08-28 修「保存并重拼整图直接报错」:此前调的 `updateAssetOverlay`
      // 打的是 `PATCH /poster/tasks/{taskId}/assets/{assetId}/overlay` —— 这个端点在
      // Phase D(2026-05-16)已被后端下线,现网只剩 `PUT /poster/assets/{assetId}/overlay`
      // (真机日志实证:PageNotFound「No mapping for PATCH …/overlay」→ 404)。
      // 海报单图编辑器早已切到 `saveAssetOverlay`(AssetEditor.tsx),长图这颗漏切。
      // 后端 saveOverlay 同步 composeAsset 写 finalKey,restitch 再拼即取到新字节。
      await posterService.saveAssetOverlay(asset.id, {
        overlayConfig: overlay,
        filterConfig: filter,
      });
      await posterService.restitchLongImage(taskId);
      toast({
        title: "已保存,正在重拼整图",
        description: "只重新拼接,不重新出图 —— 不花积分。",
      });
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "保存失败",
        description: errorToast(e).description,
      });
    } finally {
      setBusy(false);
    }
  }

  /**
   * 改内容 → 落库 + 按块类型分两档派发。
   *
   * ★**花不花钱由后端裁决**(返回体的 `tier`),这里只负责如实说出来。前端自己判一遍、
   *   后端再判一遍,迟早出现「界面说不花钱、实际扣了」。
   */
  async function saveContent() {
    if (!blockSpec || !draftProps) return;
    setBusy(true);
    try {
      const res = await posterService.applyLongImageBlockEdit(
        taskId,
        blockSpec.id,
        draftProps,
        contentFromProps(blockSpec.styleTag, draftProps) as unknown as Record<string, unknown>
      );
      toast({
        title: res.tier === "paid" ? "已开始重出这一块" : "已按新内容重渲这一块",
        description:
          res.tier === "paid"
            ? `「${blockName}」是图块,文案要 AI 画进画面 —— 按单块计一笔出图。`
            : `「${blockName}」是文字/数据块,只重渲重拼 —— 不花积分。`,
      });
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "改内容失败",
        description: errorToast(e).description,
      });
    } finally {
      setBusy(false);
    }
  }

  /** AI 改图 / AI 重画 —— 都落在长图自己的单块重出上(见文件头注释)。 */
  async function regen(instruction: string, mode: "edit" | "semantic") {
    if (!blockSpec) return;
    setBusy(true);
    try {
      await posterService.regenerateLongImageBlock(taskId, blockSpec.id, { instruction, mode });
      toast({
        title: mode === "edit" ? "已开始改这一块" : "已开始重画这一块",
        description: "只重出这一块并重拼整图,其余块不重跑。按单块计一笔出图。",
      });
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "提交失败",
        description: errorToast(e).description,
      });
    } finally {
      setBusy(false);
    }
  }

  const hasBrandLibrary = (brandAssets?.logos.length ?? 0) + (brandAssets?.qrs.length ?? 0) > 0;
  const inspectorBlock: LongImageBlockForm | null =
    blockSpec && draftProps ? { ...blockSpec, props: draftProps } : (blockSpec ?? null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* ★全屏而不是 800 抽屉:1500×320 的参数带在 800 的列里只剩 ~780 可用宽,
          拖 logo 的精度明显下降;而三 tab + 表单在 560 里更是挤不下。
          关掉就回到看图台,「还在同一个页面上」的感觉不破。 */}
      {/* ★**只封顶不钉死高**(2026-08-08 看真截图改的):原来写的是 `h-[92dvh]`,
          于是 1500×320 的参数带在 650 高的框里只占 154 高、**下面全是死白**,
          弹框看着像坏了。改成 `max-h`:高块照样吃满,扁带就缩成它该有的样子。 */}
      <DialogContent
        className="max-h-[92dvh] w-[96vw] max-w-[1400px]"
        data-testid="segment-editor-dialog"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-base">改这一块 · {blockName}</DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            改完只影响这一块,再把整图重新拼一次 —— 其余块一个字节都不动。
          </DialogDescription>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
          {/* ── 左:看这一块 ── */}
          <div className="flex min-h-0 flex-col gap-2 overflow-y-auto">
            {/* ★高度封顶用 **dvh 直接钉在内容上**,不靠 `flex-1` + `max-h-full`(2026-08-08
                看真截图两轮改出来的):弹框改成 `max-h` 之后没有确定高度可分,`flex-1`
                失去参照 → 1500×2000 的图块把弹框顶爆、右下角连关闭键都挤出视口。
                钉在内容上,扁带缩得下、高块也不会溢出。 */}
            <div
              className="flex items-center justify-center rounded-lg border border-border/60 bg-muted/30 p-3"
              style={{ filter: comparing ? "none" : filterToCss(filter) }}
            >
              {asset.baseUrl ? (
                draggable && tab === "appearance" ? (
                  <div className="max-h-[68dvh] w-full max-w-[720px] overflow-y-auto">
                    <DragOverlayCanvas
                      baseImageUrl={asset.baseUrl}
                      brandAssets={{
                        logoUrl: logoUrl ?? undefined,
                        qrUrl: qrUrl ?? undefined,
                        fallbackLogo: false,
                        fallbackQr: false,
                      }}
                      value={comparing ? readOverlay(asset) : overlay}
                      onChange={setOverlay}
                      readOnly={comparing}
                    />
                  </div>
                ) : (
                  <div className="w-full space-y-2">
                    <img
                      src={asset.baseUrl}
                      alt={blockName}
                      className="mx-auto max-h-[68dvh] w-full max-w-[720px] rounded object-contain"
                      data-testid="segment-editor-preview"
                    />
                    {tab === "appearance" && !draggable && (
                      <p className="text-center text-2xs text-muted-foreground">
                        这一块太扁({asset.actualWidth}×{asset.actualHeight}),拖 logo 会压住文字 ——
                        只开放画面调节。
                      </p>
                    )}
                  </div>
                )
              ) : (
                <div className="py-10 text-xs text-muted-foreground">这一块没有底图,改不了</div>
              )}
            </div>
            {tab === "appearance" && (
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onMouseDown={() => setComparing(true)}
                  onMouseUp={() => setComparing(false)}
                  onMouseLeave={() => setComparing(false)}
                  onTouchStart={() => setComparing(true)}
                  onTouchEnd={() => setComparing(false)}
                >
                  按住对比原图
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                  disabled={busy}
                  onClick={() => {
                    setOverlay(readOverlay(asset));
                    setFilter(asset.currentFilterConfig ?? {});
                  }}
                >
                  重置
                </Button>
              </div>
            )}
          </div>

          {/* ── 右:操作台 ── */}
          <div className="flex min-h-0 flex-col">
            <Tabs
              value={tab}
              onValueChange={(v) => setTab(v as TabKey)}
              className="flex min-h-0 flex-1 flex-col"
            >
              <TabsList className="shrink-0">
                <TabsTrigger value="appearance" className="text-xs">
                  画面调整
                </TabsTrigger>
                {canEditContent && (
                  <TabsTrigger value="content" className="text-xs">
                    改内容
                  </TabsTrigger>
                )}
                {isImageBlock && (
                  <>
                    <TabsTrigger value="edit" className="text-xs">
                      AI 改图
                    </TabsTrigger>
                    <TabsTrigger value="redraw" className="text-xs">
                      AI 重画
                    </TabsTrigger>
                  </>
                )}
              </TabsList>

              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                <TabsContent value="appearance" className="mt-3 space-y-3">
                  {/* ★挂载入口(2026-08-08):整图品牌砍掉之后,这里是**唯一**能给长图挂
                      logo / 二维码的地方。此前的抽屉只会读 `overlay.logoUploadId`、
                      没有挑选入口 —— 画布上永远没东西可拖,「在这一块上拖 logo」是句空话。
                      ★太扁的块不给挂:1500×320 的参数带上叠 logo 必然压字(拖也拖不开)。 */}
                  {
                    draggable ? (
                      hasBrandLibrary ? (
                        <div className="space-y-1.5">
                          <AttachLogoButton
                            value={overlay}
                            onChange={setOverlay}
                            canvasAspect={aspect}
                          />
                          <AttachQrButton
                            value={overlay}
                            onChange={setOverlay}
                            canvasAspect={aspect}
                          />
                        </div>
                      ) : (
                        <p className="text-2xs text-muted-foreground">
                          这个租户还没上传过 logo / 二维码,画布上没有可拖的东西 ——
                          先去「品牌资产」传一个。
                        </p>
                      )
                    ) : null /* 太扁的块:解释挂在左边画布正下方(还带真实像素),
                              这里再说一遍就是同一句话说两遍 —— 真截图上一眼看出来的 */
                  }
                  <AppearanceTab filter={filter} onChange={setFilter} />
                </TabsContent>

                {canEditContent && inspectorBlock && (
                  <TabsContent value="content" className="mt-3 space-y-3">
                    {/* ★这一段说清「改完花不花钱」——两档差 700 倍,不能让人点了才知道 */}
                    <p className="rounded bg-muted/40 p-2 text-2xs leading-relaxed text-muted-foreground">
                      {isImageBlock
                        ? "这是图块 —— 文案要由 AI 画进画面,改完会按单块重出一张(计一笔出图积分)。"
                        : "这是文字/数据块 —— 模板确定性渲染,改完只重渲重拼,不花积分。"}
                    </p>
                    <BlockInspector
                      block={inspectorBlock}
                      onPropsChange={(patch) =>
                        setDraftProps((prev) => ({ ...(prev ?? blockSpec?.props ?? {}), ...patch }))
                      }
                      onRename={() => {
                        /* 块名是这一稿的结构,不在结果页改 —— 改了整图上也看不出来 */
                      }}
                    />
                  </TabsContent>
                )}

                {isImageBlock && (
                  <>
                    <TabsContent value="edit" className="mt-3">
                      <I2IEditTab
                        busy={busy}
                        hasBaseImage={!!asset.baseUrl}
                        replaceOnly
                        credits={regenCredits}
                        onSubmit={({ instruction }) => void regen(instruction, "edit")}
                      />
                    </TabsContent>
                    <TabsContent value="redraw" className="mt-3">
                      <RegenerateTab
                        busy={busy}
                        replaceOnly
                        credits={regenCredits}
                        onRegenerate={({ instruction }) =>
                          void regen(instruction ?? "", "semantic")
                        }
                      />
                    </TabsContent>
                  </>
                )}
              </div>
            </Tabs>

            {/* 主按钮焊在列底 —— 两个 AI tab 自带提交,这里只服务前两个 tab */}
            {(tab === "appearance" || tab === "content") && (
              <div className="mt-3 flex shrink-0 items-center gap-2 border-t border-border/60 pt-3">
                <span className="flex-1" />
                {tab === "appearance" ? (
                  <Button
                    size="sm"
                    className="gap-1.5 text-xs"
                    disabled={busy || !asset.baseUrl}
                    onClick={() => void saveAppearance()}
                    data-testid="segment-editor-save-appearance"
                  >
                    {busy && <Loader2 className="h-3 w-3 animate-spin" />}
                    保存并重拼整图(免费)
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="gap-1.5 text-xs"
                    disabled={busy || draftProps == null}
                    onClick={() => void saveContent()}
                    data-testid="segment-editor-save-content"
                  >
                    {busy && <Loader2 className="h-3 w-3 animate-spin" />}
                    {isImageBlock ? "保存并重出这一块" : "保存并重渲这一块(免费)"}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** 资产上已存的 overlay(形状不保证,坏值回落默认)。 */
function readOverlay(a: PosterAssetDto): OverlayConfigV6 {
  const raw = a.currentOverlayConfig as unknown;
  if (raw && typeof raw === "object" && (raw as OverlayConfigV6).version === 1) {
    return raw as OverlayConfigV6;
  }
  return { ...DEFAULT_OVERLAY, logo: null, qr: null };
}
